<ng-container *ngIf="isNonDesktop; else settingsPanelContent">
  <app-pencil-bottom-sheet-container
    class="user-settings-bottom-sheet"
    [title]="activePanelData?.name ?? ('Settings' | translate)"
    [hasArrowBack]="!!activePanelData"
    [colorScheme]="ColorScheme.LIGHT"
    [fullscreen]="true"
    (closeClicked)="closePanel()"
    (backClicked)="switchSubPanel()"
  >
    <ng-container *ngTemplateOutlet="settingsPanelContent"></ng-container>
  </app-pencil-bottom-sheet-container>
</ng-container>

<ng-template #settingsPanelContent>
  <div id="user-settings-panel" [ngClass]="{ subPanelOpen: !!activePanelData, rtl: isRTL }">
    <div class="side">
      <div class="user" *ngIf="!isNonDesktop">
        <app-profile-photo
          [isClickEnabled]="false"
          [fromQuestionList]="false"
          [showName]="true"
          [width]="30"
        ></app-profile-photo>
      </div>
      <div class="settings-nav">
        <div class="nav-header">
          {{ 'account' | translate }}
        </div>
        <ul>
          <li
            [class.active]="activePanelData?.id === PANELS.ACCOUNT.id"
            role="button"
            (click)="switchSubPanel(PANELS.ACCOUNT.id)"
            data-name="spaces-manager-user-avatar-menu-settings-account"
          >
            <span class="icon material-icons-outlined">person</span>
            <span class="name">{{ PANELS.ACCOUNT.name }}</span>
          </li>
          <li
            *ngIf="!user?.institution && isBillingEnabled && !isNativeApp"
            id="btn-settings-billing"
            class="white-space-nowrap"
            [class.active]="activePanelData?.id === PANELS.PAYMENT.id"
            role="button"
            (click)="switchSubPanel(PANELS.PAYMENT.id)"
            data-name="spaces-manager-user-avatar-menu-settings-payment"
          >
            <span class="icon material-icons-outlined">payment</span>
            <span class="name">{{ PANELS.PAYMENT.name }}</span>
            <span
              class="billing-status-badge cml-8"
              [class.trial]="currentBilling === BillingStatus.TRIAL"
              [class.free]="currentBilling === BillingStatus.FREE"
              [class.pro]="currentBilling === BillingStatus.PAID"
              >{{
                currentBilling === BillingStatus.PAID
                  ? ('PRO' | translate)
                  : (currentBilling | translate)
              }}</span
            >
          </li>
          <li
            [class.active]="activePanelData?.id === PANELS.LANGUAGE.id"
            role="button"
            (click)="switchSubPanel(PANELS.LANGUAGE.id)"
            data-name="spaces-manager-user-avatar-menu-settings-language"
          >
            <span class="icon material-icons-outlined">language</span>
            <span class="name">{{ PANELS.LANGUAGE.name }}</span>
          </li>
          <li
            *ngIf="!isInstitutionUser && inactivityNotificationsEnabled"
            [class.active]="activePanelData?.id === PANELS.ADVANCED.id"
            role="button"
            (click)="switchSubPanel(PANELS.ADVANCED.id)"
            data-name="spaces-manager-user-avatar-menu-settings-account"
          >
            <span class="icon material-icons-outlined">tune</span>
            <span class="name">{{ PANELS.ADVANCED.name }}</span>
          </li>
        </ul>
        <ng-container *ngIf="showInstitutionSettings">
          <div class="nav-header">
            {{ 'institution' | translate }}
          </div>
          <ul>
            <li
              [class.active]="activePanelData?.id === PANELS.BILLABLE_USAGE.id"
              role="button"
              (click)="switchSubPanel(PANELS.BILLABLE_USAGE.id)"
              data-name="user-settings-community-billable_usage"
              *ngIf="
                isBillableUsageSettingEnabled &&
                !hasBillingStrategyWithHiddenBillingUi &&
                !isNativeApp
              "
            >
              <app-pencil-icon
                class="icon"
                [name]="
                  activePanelData?.id === PANELS.BILLABLE_USAGE.id
                    ? 'credit_card_blue'
                    : 'credit_card'
                "
                [type]="IconTypes.svgIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.BILLABLE_USAGE.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.ALERTS.id"
              role="button"
              (click)="switchSubPanel(PANELS.ALERTS.id)"
              data-name="user-settings-alerts"
              *ngIf="!isNonDesktop && isAlertsEnabled"
            >
              <app-pencil-icon
                class="icon"
                [name]="'triangle-alert'"
                [type]="IconTypes.lucideIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.ALERTS.name }}</span>
            </li>

            <li
              [class.active]="activePanelData?.id === PANELS.PERMISSIONS.id"
              role="button"
              (click)="switchSubPanel(PANELS.PERMISSIONS.id)"
              data-name="user-settings-community-permissions"
            >
              <span class="icon material-icons-outlined">lock_open</span>
              <span class="name">{{ PANELS.PERMISSIONS.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.BRANDING.id"
              role="button"
              (click)="switchSubPanel(PANELS.BRANDING.id)"
              data-name="user-settings-community-branding"
              *ngIf="isBrandingEnabled"
            >
              <span class="icon material-icons-outlined">palette</span>
              <span class="name">{{ PANELS.BRANDING.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.SITES_MANAGEMENT.id"
              role="button"
              (click)="switchSubPanel(PANELS.SITES_MANAGEMENT.id)"
              data-name="user-settings-community-sites-management"
              *ngIf="!isNonDesktop && isSitesEnabled"
            >
              <app-pencil-icon
                class="pencil-icon"
                [name]="
                  activePanelData?.id === PANELS.SITES_MANAGEMENT.id
                    ? 'site_management_blue'
                    : 'site_management'
                "
                [type]="IconTypes.svgIcon"
                style="--pencil-icon-size: 24px"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.SITES_MANAGEMENT.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.CUSTOM_ATTRIBUTES.id"
              role="button"
              *ngIf="isCustomAttributesEnabled && !isNonDesktop"
              (click)="switchSubPanel(PANELS.CUSTOM_ATTRIBUTES.id)"
              data-name="user-settings-community-custom-attributes"
            >
              <app-pencil-icon
                class="icon"
                [name]="
                  activePanelData?.id === PANELS.CUSTOM_ATTRIBUTES.id
                    ? 'person_play_blue'
                    : 'person_play'
                "
                [type]="IconTypes.svgIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.CUSTOM_ATTRIBUTES.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.AI_ASSIST.id"
              role="button"
              (click)="switchSubPanel(PANELS.AI_ASSIST.id)"
              data-name="user-settings-ai-assist"
            >
              <span class="icon material-icons">auto_fix_high</span>
              <span class="name">{{ PANELS.AI_ASSIST.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.FEEDBACK.id"
              role="button"
              (click)="switchSubPanel(PANELS.FEEDBACK.id)"
              data-name="user-settings-feedback"
              *ngIf="isSchedulingEnabled && !isNonDesktop"
            >
              <app-pencil-icon
                class="icon"
                [name]="
                  activePanelData?.id === PANELS.FEEDBACK.id ? 'rate_review_blue' : 'rate_review'
                "
                [type]="IconTypes.svgIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.FEEDBACK.name }}</span>
            </li>

            <li
              *ngIf="!isNonDesktop"
              [class.active]="activePanelData?.id === PANELS.INTEGRATIONS.id"
              role="button"
              (click)="switchSubPanel(PANELS.INTEGRATIONS.id)"
              data-name="user-settings-integrations"
            >
              <span class="icon material-icons-outlined">vpn_key</span>
              <span class="name">{{ PANELS.INTEGRATIONS.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.EVENTS.id"
              role="button"
              (click)="switchSubPanel(PANELS.EVENTS.id)"
              data-name="user-settings-events"
              *ngIf="isSchedulingEnabled"
            >
              <app-pencil-icon
                class="icon"
                [name]="
                  activePanelData?.id === PANELS.EVENTS.id ? 'calendar_blue' : 'calendar_black'
                "
                [type]="IconTypes.svgIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.EVENTS.name }}</span>
            </li>
            <li
              [class.active]="activePanelData?.id === PANELS.SESSIONS.id"
              role="button"
              (click)="switchSubPanel(PANELS.SESSIONS.id)"
              data-name="user-settings-sessions"
              *ngIf="isNewSessionsFlowEnabled"
            >
              <app-pencil-icon
                class="icon"
                [name]="'clipboard-clock'"
                [type]="IconTypes.lucideIcon"
              ></app-pencil-icon>
              <span class="name">{{ PANELS.SESSIONS.name }}</span>
            </li>
          </ul>
        </ng-container>
      </div>
    </div>
    <div class="main" [ngClass]="{ isNonDesktop }">
      <!-- Settings Sub Panels -->
      <app-user-settings-account
        *ngIf="activePanelData?.id === PANELS.ACCOUNT.id"
        [form]="accountForm"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-account>
      <app-user-settings-payment
        *ngIf="activePanelData?.id === PANELS.PAYMENT.id && isBillingEnabled"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        (goBack)="switchSubPanel()"
        (closePanel)="closePanel()"
      ></app-user-settings-payment>
      <app-user-settings-language
        *ngIf="activePanelData?.id === PANELS.LANGUAGE.id"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        (goBack)="switchSubPanel()"
      ></app-user-settings-language>
      <app-user-settings-advanced
        *ngIf="activePanelData?.id === PANELS.ADVANCED.id"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        [form]="advancedSettingsForm"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-advanced>
      <app-user-settings-permissions
        *ngIf="activePanelData?.id === PANELS.PERMISSIONS.id"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        [form]="institutionForm"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-permissions>
      <app-user-settings-sites-management
        *ngIf="activePanelData?.id === PANELS.SITES_MANAGEMENT.id"
        [panelData]="PANELS.SITES_MANAGEMENT"
      ></app-user-settings-sites-management>
      <app-user-settings-institution-custom-attributes
        *ngIf="activePanelData?.id === PANELS.CUSTOM_ATTRIBUTES.id && !isNonDesktop"
      ></app-user-settings-institution-custom-attributes>
      <app-user-settings-ai-assist
        *ngIf="activePanelData?.id === PANELS.AI_ASSIST.id"
        [isMobile]="isNonDesktop"
        [PANELS]="PANELS"
        [form]="aiAssistForm"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-ai-assist>
      <app-user-settings-billable-usage
        *ngIf="activePanelData?.id === PANELS.BILLABLE_USAGE.id"
        [isMobile]="isNonDesktop"
        [panelData]="PANELS.BILLABLE_USAGE"
        (goBack)="switchSubPanel()"
      ></app-user-settings-billable-usage>
      <app-user-settings-feedback
        *ngIf="activePanelData?.id === PANELS.FEEDBACK.id"
        [panelData]="PANELS.FEEDBACK"
        [form]="feedbackForm"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-feedback>
      <app-user-settings-branding
        *ngIf="activePanelData?.id === PANELS.BRANDING.id"
        [panelData]="PANELS.BRANDING"
        [form]="brandingForm"
        [isMobile]="isNonDesktop"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-branding>
      <app-user-settings-integrations
        *ngIf="activePanelData?.id === PANELS.INTEGRATIONS.id"
        [PANELS]="PANELS"
        [form]="institutionForm"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-integrations>
      <app-user-settings-events
        *ngIf="activePanelData?.id === PANELS.EVENTS.id"
        [panelData]="PANELS.EVENTS"
        [form]="eventsForm"
        [isMobile]="isNonDesktop"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-events>
      <app-user-settings-alerts
        *ngIf="activePanelData?.id === PANELS.ALERTS.id"
        [panelData]="PANELS.ALERTS"
        [isMobile]="isNonDesktop"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-alerts>
      <app-user-settings-sessions
        *ngIf="activePanelData?.id === PANELS.SESSIONS.id && isNewSessionsFlowEnabled"
        [panelData]="PANELS.SESSIONS"
        [form]="sessionsForm"
        [isMobile]="isNonDesktop"
        (goBack)="switchSubPanel()"
        (formChangedEvent)="handleFormChanged($event)"
      ></app-user-settings-sessions>
    </div>
    <app-pencil-button
      *ngIf="!isNonDesktop"
      class="pencil-button close-panel"
      (clickEvent)="closePanel()"
      data-name="spaces-manager-user-avatar-menu-settings-close"
      icon="close"
      [iconConfig]="{ type: 'mat-icon' }"
    >
    </app-pencil-button>
  </div>
</ng-template>
